import { useState, type KeyboardEvent } from 'react';
import { Controller, type FieldValues } from 'react-hook-form';

import { cn } from '~/utils/cn';
import { applyMask } from '~/utils/masks';

import { Box } from '../Box/view';
import { Icon } from '../Icon/view';
import { Text } from '../Text/view';
import type { PropsTextInput } from './interfaces';

export default function TextInput<T extends FieldValues>({
  control,
  name,
  icon,
  rightIcon,
  placeholder,
  label,
  secureTextEntry,
  keyboardType = 'default',
  autoCapitalize = 'none',
  autoComplete,
  disabled,
  defaultValue,
  value,
  onChangeText,
  onBlur,
  onSubmitEditing,
  onClickRightIcon,
  errorMessage,
  className,
  inputRef,
  multiline,
  rows = 3,
  maxLength,
  inputMode,
  mask,
}: PropsTextInput<T>) {
  const formatValue = (raw: string): string =>
    mask ? applyMask(mask, raw) : raw;
  const [revealed, setRevealed] = useState(false);
  const isPassword = !!secureTextEntry;
  const type = isPassword && !revealed ? 'password' : 'text';

  const typeForKeyboard =
    keyboardType === 'email-address'
      ? 'email'
      : keyboardType === 'numeric' || keyboardType === 'phone-pad'
        ? 'tel'
        : type;

  const inferredInputMode =
    inputMode ??
    (keyboardType === 'email-address'
      ? 'email'
      : keyboardType === 'numeric'
        ? 'numeric'
        : keyboardType === 'phone-pad'
          ? 'tel'
          : undefined);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !multiline && onSubmitEditing) {
      e.preventDefault();
      onSubmitEditing();
    }
  };

  const renderInput = (
    fieldValue: string,
    onChange: (val: string) => void,
    onBlurField?: () => void,
  ) => {
    const sharedClasses = cn(
      'flex-1 bg-transparent outline-none text-bodyMD text-complement-01 placeholder:text-intense-05',
      multiline ? 'resize-none' : '',
    );

    if (multiline) {
      return (
        <textarea
          ref={inputRef as React.Ref<HTMLTextAreaElement>}
          rows={rows}
          value={fieldValue}
          placeholder={placeholder}
          onChange={(e) => onChange(formatValue(e.target.value))}
          onBlur={onBlurField}
          disabled={disabled}
          maxLength={maxLength}
          className={sharedClasses}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type={typeForKeyboard}
        inputMode={inferredInputMode}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        value={fieldValue}
        placeholder={placeholder}
        onChange={(e) => onChange(formatValue(e.target.value))}
        onBlur={onBlurField}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        maxLength={maxLength}
        className={sharedClasses}
      />
    );
  };

  const containerClasses = cn(
    'flex items-center gap-nano px-small h-[56px] rounded-small bg-intense-02 border',
    errorMessage ? 'border-warning-04' : 'border-intense-03',
    disabled && 'opacity-60',
    multiline && 'h-auto py-small',
    className,
  );

  return (
    <Box className="flex flex-col gap-quark w-full">
      {label && (
        <Text className="text-bodyMD color-intense-07">{label}</Text>
      )}
      {control && name ? (
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue as never}
          render={({ field }) => (
            <div className={containerClasses}>
              {icon && <Icon name={icon} size={20} color="#606060" />}
              {renderInput(field.value ?? '', field.onChange, field.onBlur)}
              {(isPassword || rightIcon) && (
                <Icon
                  name={
                    isPassword
                      ? revealed
                        ? 'icon-eye-hide'
                        : 'icon-eye'
                      : rightIcon
                  }
                  size={20}
                  color="#606060"
                  onClick={
                    isPassword
                      ? () => setRevealed((p) => !p)
                      : onClickRightIcon
                  }
                />
              )}
            </div>
          )}
        />
      ) : (
        <div className={containerClasses}>
          {icon && <Icon name={icon} size={20} color="#606060" />}
          {renderInput(
            value ?? '',
            (val) => onChangeText?.(val),
            onBlur,
          )}
          {(isPassword || rightIcon) && (
            <Icon
              name={
                isPassword
                  ? revealed
                    ? 'icon-eye-hide'
                    : 'icon-eye'
                  : rightIcon
              }
              size={20}
              color="#606060"
              onClick={
                isPassword ? () => setRevealed((p) => !p) : onClickRightIcon
              }
            />
          )}
        </div>
      )}
      {errorMessage && (
        <Text className="text-captionMD color-warning-06">{errorMessage}</Text>
      )}
    </Box>
  );
}
